import React from 'react';

// THE ranked queue of proposals, first and loudest on the page.
//
// A proposal is a COMPANY AND THE PAGES IT WOULD BE WATCHED THROUGH, decided in one
// action. It used to be two chained decisions — "start monitoring Central Group",
// then separately "add centralgroup.com" — and accepting the first left a company
// switched on and watching nothing until the user came back for the second.
// Measured live, the owner accepted 31 companies and ended up with 0 sources.
//
// ONE list, not two. An earlier cut split it by whether the company had a page on
// record: 167 "ready to watch" against 259 "needs a page from you". That organised
// the imbalance rather than removing it — 61% of the queue became a place you went
// instead of something you did, and the ranking was cut in half so the single
// strongest company on the tenant (73 open roles, no page on file) sat in the lower
// list. Here a row with no page carries a URL field instead of a page list, so the
// ask is answered where it is made and the ranking stays whole.
//
// The ranking score is never shown (parent spec SS6). The reasons are what a user
// judges by — a proposal that cannot explain itself cannot be judged.

// absoluteURL adds the scheme a pasted URL is usually missing. The API refuses
// anything that is not an absolute http(s) URL, so without this "linkedin.com/x" —
// the form people actually paste — comes back as a 400 for typing what they were
// asked for. Returns '' when there is nothing usable to make absolute.
export function absoluteURL(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

// pageTypeOf reads the kind of page from the URL's HOST, because the type decides
// which column on app.companies the value is written to — a LinkedIn filed as the
// company's website would corrupt the field for every reader of it, the paid
// discovery enricher included.
//
// The host is parsed rather than pattern-matched against the whole string: a first
// version tested /(^|\.)linkedin\.com/ and missed "https://linkedin.com/..."
// entirely (the character before the host is a slash), while a looser pattern would
// have called "https://example.com/?ref=linkedin.com" a LinkedIn. Anything that is
// not a linkedin.com host is a website, which is what a user pasting "their page"
// almost always means.
export function pageTypeOf(url) {
  try {
    const host = new URL(absoluteURL(url)).hostname.toLowerCase();
    return host === 'linkedin.com' || host.endsWith('.linkedin.com') ? 'linkedin' : 'website';
  } catch {
    return 'website';
  }
}

// The reason codes the evidence endpoint can answer for. The others state their own
// evidence in the sentence itself, so offering a drill-down would open a dialog onto
// a single row repeating what the user just read — and the API answers 400 for them.
export const REASONS_WITH_EVIDENCE = new Set([
  'open_role_volume',
  'contact_density',
  'recorded_contact',
]);

const PAGE_TYPE_LABEL = {
  website: 'Website',
  linkedin: 'LinkedIn',
  careers: 'Careers',
  news: 'News',
  other: 'Page',
};

// PageList is what makes a bundle judgeable. Naming the company alone asks the user
// to trust that a sensible page exists; showing the URLs lets them see what they are
// agreeing to watch before they agree to it.
function PageList({ pages }) {
  if (!pages || pages.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {pages.map((pg) => (
        <li key={pg.url} className="flex items-baseline gap-2 text-sm">
          <span className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-gray-500">
            {PAGE_TYPE_LABEL[pg.source_type] || pg.source_type}
          </span>
          <span className="font-mono text-[13px] text-gray-700 break-all">{pg.url}</span>
        </li>
      ))}
    </ul>
  );
}

// SupplyPage is the whole of Option B in one control.
//
// A company with no page used to live in a separate list whose only action sent you
// to a dialog. Measured on the dev tenant that was 259 of 425 companies — 61% of the
// queue was a place you went rather than something you did. Answering the ask in the
// row removes the category instead of organising it: paste a URL, and the company is
// watched by the same button that watches every other row.
function SupplyPage({ proposal, onSupplyPage, busy }) {
  const [url, setUrl] = React.useState('');
  const submit = () => {
    const v = absoluteURL(url);
    if (!v) return;
    onSupplyPage(proposal, v, pageTypeOf(v));
  };
  return (
    <div className="mt-2.5">
      {/* The reasons above prove the company is worth attention; this says what the
          missing page costs and what supplying it buys. Deliberately generic and
          free of any strength claim — it renders on EVERY pageless row, including a
          company with one dormant role, and "strong signal" there would be exactly
          the inflation the reason-builder refuses. It points at the evidence
          already on screen instead. */}
      <p className="text-[12.5px] text-gray-600 mb-2">
        Worth watching on the signal above — but with no website or LinkedIn on record
        we can’t follow what they do next.
      </p>
      <div className="flex gap-2 flex-wrap">
        <input
          type="url"
          value={url}
          disabled={busy}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          aria-label={`Page URL for ${proposal.company}`}
          placeholder="Paste their website or LinkedIn…"
          className="flex-1 min-w-[200px] font-mono text-[13px] px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-gray-900 placeholder:font-sans placeholder:text-gray-400 focus:outline-none focus:border-solid focus:border-[#00C6AE] focus:bg-white disabled:opacity-50"
        />
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b] disabled:opacity-50"
        >
          Watch
        </button>
      </div>
      <p className="mt-1.5 text-[11.5px] text-gray-400">
        Add one and we’ll watch it from here — new roles, news and people moving.
      </p>
    </div>
  );
}

// dismissLabel names what the button actually does, because the two meanings are not
// interchangeable. Refusing a company nobody has decided about takes it off the list
// for good; refusing a page on a company the user deliberately switched on must only
// drop that page. A single shared label would make the destructive one invisible.
function dismissLabel(proposal) {
  if (!proposal.monitored) return 'Not this company';
  return (proposal.pages || []).length > 1 ? 'Skip these pages' : 'Skip this page';
}

function ProposalRow({ proposal, onAccept, onDismiss, busy, onShowEvidence, onSupplyPage }) {
  const hasPages = (proposal.pages || []).length > 0;

  return (
    <div
      data-testid="proposal-row"
      className="flex gap-4 items-start px-4 py-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">{proposal.company}</div>
        <ul className="mt-1.5 space-y-1">
          {proposal.reasons.map((r) => {
            // Only reasons with records behind them are clickable. The others state
            // their own evidence in the sentence, so a dialog would show one row
            // repeating what was just read.
            const drillable = onShowEvidence && REASONS_WITH_EVIDENCE.has(r.code);
            return (
              <li key={r.code} className="text-sm text-gray-700 flex gap-2 items-start">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00C6AE] shrink-0" aria-hidden="true" />
                <span>
                  {r.text}
                  {drillable && (
                    // A real button, not an underline: checking the evidence is a
                    // first-class action here, and a dotted underline reads as
                    // decoration that a user has to discover by hovering.
                    <button
                      type="button"
                      onClick={() => onShowEvidence(proposal, r)}
                      aria-label={`Show the records behind: ${r.text}`}
                      className="ml-2 inline-flex items-center gap-1 align-[1px] rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-gray-600 hover:border-[#00C6AE] hover:text-[#00695C] hover:bg-teal-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00C6AE]"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="3" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" />
                        <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                      </svg>
                      Show
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        <PageList pages={proposal.pages} />
        {!hasPages && onSupplyPage && (
          <SupplyPage proposal={proposal} onSupplyPage={onSupplyPage} busy={busy} />
        )}
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        {hasPages && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAccept(proposal)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b] disabled:opacity-50"
          >
            Watch
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => onDismiss(proposal)}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-50"
        >
          {dismissLabel(proposal)}
        </button>
      </div>
    </div>
  );
}

// WatchAllConfirm gates the bulk accept.
//
// Switching companies on en masse is reversible per company but tedious to undo, so
// it does not happen on a stray click. The count is stated, and when the surface is
// only a page of a longer backlog that is said too — calling it "all" while hundreds
// wait would overstate what the button did.
function WatchAllConfirm({ count, mayHaveMore, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-6" role="dialog" aria-modal="true" aria-label="Start watching every company on this list">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl overflow-hidden">
        <header className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Watch {count} companies?</h3>
        </header>
        <div className="px-5 py-4 space-y-2 text-sm text-gray-600">
          <p>
            SignalCue will start watching all {count} companies on this list, through the pages
            shown under each. You can switch any of them off individually afterwards.
          </p>
          {mayHaveMore && (
            <p className="text-gray-500">
              This is the top of the list — more companies may still be waiting after these.
            </p>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600">
            Cancel
          </button>
          <button type="button" onClick={onConfirm}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b]">
            Watch {count} companies
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function ProposalQueue({
  proposals, onAccept, onDismiss, busyKeys = new Set(),
  onWatchAll, mayHaveMore = false, watchAllBusy = false, onShowEvidence, onSupplyPage, total,
}) {
  // Session-scoped, deliberately NOT persisted. The queue is the point of this
  // page, and a preference remembered across visits would let someone collapse it
  // once and never see a proposal again — the product would look finished when it
  // is full. Collapsing is for getting it out of the way now.
  const [open, setOpen] = React.useState(true);
  const [confirmingAll, setConfirmingAll] = React.useState(false);

  if (!proposals.length) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 mb-6">
        Nothing waiting — every company we can see has been decided.
      </div>
    );
  }

  // Bulk accept covers ONLY the rows that carry pages. The rest are waiting on a URL
  // that only the user has, so sweeping them would mean switching companies on with
  // nothing to watch — the dead end this whole redesign removes. The count and the
  // label both say "with pages" rather than quietly acting on a subset.
  const acceptable = proposals.filter((p) => (p.pages || []).length > 0);
  const canWatchAll = onWatchAll && acceptable.length > 0;
  // The whole backlog when the caller knows it, else what is on screen.
  const waiting = total && total > proposals.length ? total : proposals.length;

  return (
    <section className="mb-6" aria-label="Proposals">
      <div className="flex items-baseline gap-2.5 mb-2.5 flex-wrap">
        <h2 className="text-base font-semibold text-gray-900">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="ss-proposal-list"
            className="flex items-baseline gap-2 text-base font-semibold text-gray-900 hover:text-[#00695C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#00C6AE] rounded"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="3" aria-hidden="true"
              className={`shrink-0 self-center transition-transform ${open ? 'rotate-90' : ''}`}>
              <path d="M8 4l10 8-10 8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            SignalCue proposes
            {/* The count rides in the HEADER, not only in the list caption, so
                collapsing hides the work without hiding that there is work. */}
            <span className="text-sm font-medium text-[#00695C] tabular-nums">
              {waiting}
            </span>
          </button>
        </h2>
        {open && (
          <span className="text-sm text-gray-500">
            Companies worth watching. Paste a page on any row that needs one.
          </span>
        )}
        <span className="flex-1" />
        {open && canWatchAll && (
          <button
            type="button"
            disabled={watchAllBusy}
            onClick={() => setConfirmingAll(true)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#00C6AE] text-[#00695C] hover:bg-teal-50 disabled:opacity-50"
          >
            Watch all {acceptable.length} with pages
          </button>
        )}
      </div>
      {confirmingAll && (
        <WatchAllConfirm
          count={acceptable.length}
          mayHaveMore={mayHaveMore}
          onCancel={() => setConfirmingAll(false)}
          onConfirm={() => { setConfirmingAll(false); onWatchAll(acceptable); }}
        />
      )}
      {open && (
      <div id="ss-proposal-list"
        className="rounded-lg border border-amber-200 overflow-hidden bg-white">
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[10px] uppercase tracking-wider text-amber-800">
          {/* The total is the whole surface, not this page: saying "25 waiting" while
              141 remain behind the cap would read as an empty queue once worked. */}
          {total && total > proposals.length
            ? `${proposals.length} of ${total} · ranked by what they add`
            : `${proposals.length} waiting · ranked by what they add`}
        </div>
        {proposals.map((p) => (
          <ProposalRow
            key={p.dedupe_key}
            proposal={p}
            onAccept={onAccept}
            onDismiss={onDismiss}
            busy={busyKeys.has(p.dedupe_key)}
            onShowEvidence={onShowEvidence}
            onSupplyPage={onSupplyPage}
          />
        ))}
      </div>
      )}
    </section>
  );
}
